import { BitDepth, SensorRawDataFormat, SensorType, SproutSensorID } from "../shared.js";
import { SensorConfigurations } from "./SensorConfigurations/SensorConfiguration.js";

export type SproutSensorIDSpec = {
    bitDepth: BitDepth;
    hardwareType: SensorRawDataFormat;
    minSupportedSproutVersion: number;
    minValue?: number;
    maxValue?: number;
};

export const sproutSensorIDTable: Record<SproutSensorID, SproutSensorIDSpec> = {
    0: { bitDepth: 8, hardwareType: "ADC", minSupportedSproutVersion: 0, minValue: 0, maxValue: 5 },
    1: { bitDepth: 12, hardwareType: "ADC", minSupportedSproutVersion: 0, minValue: 0, maxValue: 5 },
    2: { bitDepth: 16, hardwareType: "ADC", minSupportedSproutVersion: 99, minValue: 0, maxValue: 5 },
    3: { bitDepth: 32, hardwareType: "ADC", minSupportedSproutVersion: 99, minValue: 0, maxValue: 5 },

    4: { bitDepth: 8, hardwareType: "PWM", minSupportedSproutVersion: 0, minValue: 0, maxValue: 1000 },
    5: { bitDepth: 12, hardwareType: "PWM", minSupportedSproutVersion: 0, minValue: 0, maxValue: 1000 },
    6: { bitDepth: 16, hardwareType: "PWM", minSupportedSproutVersion: 0, minValue: 0, maxValue: 1000 },
    7: { bitDepth: 32, hardwareType: "PWM", minSupportedSproutVersion: 99, minValue: 0, maxValue: 1000 },

    8: { bitDepth: 16, hardwareType: "TEMPHUM", minSupportedSproutVersion: 0 },
    9: { bitDepth: 24, hardwareType: "TEMPHUM", minSupportedSproutVersion: 99 },
    10: { bitDepth: 32, hardwareType: "TEMPHUM", minSupportedSproutVersion: 0 },
    11: { bitDepth: 64, hardwareType: "TEMPHUM", minSupportedSproutVersion: 99 },

    12: { bitDepth: 16, hardwareType: "ACCEL", minSupportedSproutVersion: 99 },
    13: { bitDepth: 24, hardwareType: "ACCEL", minSupportedSproutVersion: 99 },
    14: { bitDepth: 32, hardwareType: "ACCEL", minSupportedSproutVersion: 99 },
    15: { bitDepth: 64, hardwareType: "ACCEL", minSupportedSproutVersion: 99 },
};

//gets populated automatically from the chart above.
export const SproutSensorIDsByBitDepthByHardwareType: Record<SensorRawDataFormat, Record<BitDepth, SproutSensorID>> = {
    ACCEL: {},
    ADC: {},
    PWM: {},
    TEMPHUM: {},
    PULSE_TOTAL: {},
};
Object.keys(SproutSensorIDsByBitDepthByHardwareType).forEach((htype) => {
    const relevantIDs = Object.keys(sproutSensorIDTable).filter((id) => sproutSensorIDTable[id].hardwareType === htype);
    relevantIDs.forEach((relID) => {
        SproutSensorIDsByBitDepthByHardwareType[htype][sproutSensorIDTable[relID].bitDepth] = parseInt(relID);
    });
});

/**
 * Gets the sprout ID that should be used for a given sensor type. If a sensor type is not valid, then it
 * returns null
 * @param sensorType
 * @returns {any}
 */
export function getIdealSproutSensorIDForSensorType(sensorType: SensorType): number | null {
    const config = SensorConfigurations[sensorType];
    if (!config?.verdiDeviceSensorConfiguration) {
        return null;
    }
    const idealBitDepthsForPrecisionImportances = {
        low: 8,
        medium: 12,
        high: 16,
    };
    let idealBitDepth =
        idealBitDepthsForPrecisionImportances[config.verdiDeviceSensorConfiguration.precisionImportance] || 12;

    // these sensors normally have twice the bit depth
    if (["TEMPHUM", "ACCEL"].includes(config.verdiDeviceSensorConfiguration.rawDataFormat)) {
        idealBitDepth = idealBitDepth * 2;
    }
    const IDsByBitDepth = SproutSensorIDsByBitDepthByHardwareType[config.verdiDeviceSensorConfiguration.rawDataFormat];
    return IDsByBitDepth[idealBitDepth] || IDsByBitDepth[Object.keys(IDsByBitDepth)[1]];
}
